import { useTranslation } from 'react-i18next'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/components/ui/accordion'
import { Label } from '@/shared/components/ui/label'
import { NativeSelect } from '@/shared/components/ui/native-select'
import { useCountries } from '../../api/countryQueries'
import { BooleanFilter } from './BooleanFilter'
import { CheckboxGroup } from './CheckboxGroup'
import { SearchField } from './SearchField'
import { toggleValue } from './searchParams'
import {
  ACCESSIBILITY_FILTERS,
  AMENITY_FILTERS,
  type PropertyFiltersValues,
  type UpdateSearch,
} from './types'

type FiltersPanelProps = {
  values: PropertyFiltersValues
  updateSearch: UpdateSearch
}

export function FiltersPanel({ values, updateSearch }: FiltersPanelProps) {
  const { t } = useTranslation()
  const countriesQuery = useCountries()
  const countries = (countriesQuery.data ?? []).filter((c) => Boolean(c.code) && Boolean(c.name))
  const minPrice = Number(values.minPrice)
  const maxPrice = Number(values.maxPrice)
  const priceRangeInvalid =
    values.minPrice !== '' && values.maxPrice !== '' && Number.isFinite(minPrice) && Number.isFinite(maxPrice) && minPrice > maxPrice

  return (
    <Accordion type="multiple" defaultValue={['destination', 'price']} className="w-full">
      <AccordionItem value="destination">
        <AccordionTrigger>{t('reservation.properties.filters.destination')}</AccordionTrigger>
        <AccordionContent>
          <div className="grid gap-4">
            <div className="space-y-1">
              <Label htmlFor="filter-country">{t('reservation.properties.filters.country')}</Label>
              <NativeSelect
                id="filter-country"
                value={values.country}
                onChange={(e) => updateSearch({ country: e.target.value || null })}
              >
                <option value="">{t('reservation.properties.filters.destination_all')}</option>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="capacity">
        <AccordionTrigger>{t('reservation.properties.filters.section_capacity')}</AccordionTrigger>
        <AccordionContent>
          <div className="grid gap-4">
            <SearchField
              id="filter-bedrooms"
              label={t('reservation.properties.filters.min_bedrooms')}
              type="number"
              min={1}
              value={values.minBedrooms}
              onChange={(value) => updateSearch({ min_bedrooms: value || null })}
            />
            <SearchField
              id="filter-bathrooms"
              label={t('reservation.properties.filters.min_bathrooms')}
              type="number"
              min={1}
              value={values.minBathrooms}
              onChange={(value) => updateSearch({ min_bathrooms: value || null })}
            />
          </div>
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="price">
        <AccordionTrigger>{t('reservation.properties.filters.section_price')}</AccordionTrigger>
        <AccordionContent>
          <div className="grid gap-4">
            <SearchField
              id="filter-min-price"
              label={t('reservation.properties.filters.min_price')}
              inputMode="numeric"
              value={values.minPrice}
              onChange={(value) => updateSearch({ min_price: value || null })}
            />
            <SearchField
              id="filter-max-price"
              label={t('reservation.properties.filters.max_price')}
              inputMode="numeric"
              value={values.maxPrice}
              error={priceRangeInvalid ? t('reservation.properties.filters.price_range_invalid') : undefined}
              onChange={(value) => updateSearch({ max_price: value || null })}
            />
          </div>
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="amenities">
        <AccordionTrigger>{t('reservation.properties.filters.amenities')}</AccordionTrigger>
        <AccordionContent>
          <CheckboxGroup
            title={t('reservation.properties.filters.amenities')}
            hideTitle
            values={AMENITY_FILTERS}
            selected={values.amenities}
            labelFor={(value) => t(`reservation.properties.amenities.${value}`, { defaultValue: value })}
            onToggle={(value) =>
              updateSearch({ amenities: toggleValue(values.amenities, value).join(',') || null })
            }
          />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="accessibility">
        <AccordionTrigger>{t('reservation.properties.filters.accessibility')}</AccordionTrigger>
        <AccordionContent>
          <CheckboxGroup
            title={t('reservation.properties.filters.accessibility')}
            hideTitle
            values={ACCESSIBILITY_FILTERS}
            selected={values.accessibility}
            labelFor={(value) => t(`reservation.properties.amenities.${value}`, { defaultValue: value })}
            onToggle={(value) =>
              updateSearch({ accessibility: toggleValue(values.accessibility, value).join(',') || null })
            }
          />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="cancellation">
        <AccordionTrigger>{t('reservation.properties.filters.cancellation')}</AccordionTrigger>
        <AccordionContent>
          <fieldset className="space-y-2">
            <legend className="sr-only">{t('reservation.properties.filters.cancellation')}</legend>
            {(['', 'flexible', 'free'] as const).map((value) => (
              <label
                key={value || 'all'}
                className="flex min-h-9 cursor-pointer items-center gap-2 rounded-md border border-border px-3 text-sm"
              >
                <input
                  type="radio"
                  name="property-cancellation"
                  className="accent-primary"
                  checked={values.cancellation === value}
                  onChange={() => updateSearch({ cancellation: value || null })}
                />
                <span>{t(`reservation.properties.filters.cancellation_${value || 'all'}`)}</span>
              </label>
            ))}
          </fieldset>
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="availability">
        <AccordionTrigger>{t('reservation.properties.filters.section_availability')}</AccordionTrigger>
        <AccordionContent>
          <div className="space-y-2">
            <BooleanFilter
              id="filter-has-availability"
              checked={values.hasAvailability}
              disabled={!values.hasDateRange}
              label={t('reservation.properties.filters.has_availability')}
              hint={
                values.hasDateRange
                  ? undefined
                  : t('reservation.properties.filters.has_availability_dates_required')
              }
              onChange={(checked) => updateSearch({ has_availability: checked ? '1' : null })}
            />
            <BooleanFilter
              id="filter-only-active"
              checked={values.onlyActive}
              label={t('reservation.properties.filters.only_active')}
              onChange={(checked) => updateSearch({ only_active: checked ? null : '0' })}
            />
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
